<template>
  <Transition
    appear
    name="modal">
    <div
      :class="[
        $style['modal'],
        { [$style['modal--small']]: small },
        { [$style['modal--fullscreen']]: fullscreen },
        { [$style['modal--width-auto']]: widthAuto },
        { [$style['modal--no-padding']]: noPadding }
      ]">
      <div
        :class="$style['modal__bg']"
        @click="close" />
      <div
        ref="model__content"
        :class="$style['modal__content']">
        <div
          v-if="!hideHeader"
          :class="[
            $style['modal__header'],
            { [$style['modal__header--bordered']]: showBorder },
            { [$style['modal__header--full-width']]: hideClose },
            { [$style['modal__header--inner-shadow']]: innerShadow && hideClose }
          ]">
          <slot name="modal-header">
&nbsp;
          </slot>
        </div>

        <div
          v-if="!hideClose"
          :class="$style['modal__close']">
          <Btn
            color="white"
            size="small"
            border
            @click="close">
            {{ $t('general/close') }}
          </btn>
        </div>

        <div
          :class="[
            $style['modal__content-wrapper'],
            { [$style['modal__content-wrapper--bordered']]: showBorder }]">
          <slot />
        </div>

        <div
          :class="[
            $style['modal__footer'],
            { [$style['modal__footer--inner-shadow']]: innerShadow}]">
          <slot name="modal-footer" />
        </div>
      </div>
    </div>
  </Transition>
</template>
<script setup>
const emit = defineEmits(['close'])
defineProps({
  hideHeader: {
    type: Boolean,
    default: false
  },
  hideClose: {
    type: Boolean,
    default: false
  },
  showBorder: {
    type: Boolean,
    default: false
  },
  innerShadow: {
    type: Boolean,
    default: false
  },
  noPadding: {
    type: Boolean,
    default: false
  },
  widthAuto: {
    type: Boolean,
    default: false
  },
  small: {
    type: Boolean,
    default: false
  },
  fullscreen: {
    type: Boolean,
    default: false
  }
})

const close = () => {
  emit('close')
}
</script>

<style lang="scss" module>
.modal {
  align-items: center;
  justify-content: center;
  display: flex;
  position: fixed;
  opacity: 1;
  inset: 0;
  width: auto;
  height: auto;
  z-index: z-index(modal);
  transform: translate3d(0,0,0);
  overscroll-behavior: contain;

  @media (max-width: #{$breakpoint-mobile}) {
    align-items: flex-end;
  }

  &__bg {
    background-color: $backdrop;
    backdrop-filter: blur(1px) opacity(1);
    bottom: 0;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 1;
    position: fixed;
    z-index: 1;
    will-change: opacity;
    overscroll-behavior: none;
  }

  &__content {
    background-color: $white;
    border-radius: $border-radius;
    box-shadow: $box-shadow-large;
    display: flex;
    flex-direction: column;
    width: calc(95vw - #{$spacing * 6});
    height: auto;
    max-height: calc(100% - #{$spacing * 8});
    max-width: 800px;
    position: relative;
    padding-bottom: env(safe-area-inset-bottom);
    opacity: 1;
    z-index: 2;

    @media (max-width: #{$breakpoint-mobile}) {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      width: 100vw;
    }
  }

  &__content-wrapper {
    height: auto;
    width: 100%;
    flex: 1;
    display: inline-block;
    padding: $spacing * 2;
    -webkit-overflow-scrolling: touch;
    overflow-x: auto;

    @media (max-width: #{$breakpoint-mobile}) {
      padding: $spacing * 2;
    }

    &--bordered {
      border-top: 1px solid $grey-light;
      border-bottom: 1px solid $grey-light;
    }
  }

  &__header {
    align-items: center;
    justify-content: space-between;
    display: flex;
    padding: $spacing * 2;
    min-height: $spacing * 8;
    width: calc(100% - #{$spacing * 14});

    &--bordered {
      height: $spacing * 10;
    }

    &--full-width {
      width: 100%;
      z-index: 1;
    }

    &--inner-shadow {
      box-shadow: $box-shadow-large;
    }
  }

  &__close {
    display: flex;
    align-items: center;
    padding: $spacing * 2;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 3;
    height: $spacing * 8;

  }

  &__footer {
    &:not(:empty) {
      display: grid;
      grid-template-columns: repeat(auto-fit,minmax(120px, 1fr));
      grid-gap: $spacing;
      padding: $spacing * 2;
      padding-top: 0;
    }

    &--inner-shadow:not(:empty) {
      padding-top: $spacing;
      box-shadow: $box-shadow-large;
      z-index: 1;
    }

  }

  &--small {
    .modal__content {
      max-width: $breakpoint-mobile;
      height: auto;
    }
  }

  &--fullscreen {
    .modal__content {
      height: calc(100vh - #{$spacing*2});
    }
  }

  &--no-padding {
    .modal__header,
    .modal__content-wrapper {
      padding: 0;
    }
  }

  &--width-auto {
    .modal__content {
      width: auto;
      min-width: $spacing * 38;
      max-width: 100%;

      @media (max-width: #{$breakpoint-mobile}) {
        width: 100%;
      }
    }
  }
}

:global {
  .modal-enter-from,
  .modal-leave-to {
    :local {
      &,
      .modal__content,
      .modal__bg {
        opacity: 0;
        transition-property: transform, opacity, backdrop-filter;
        transition-duration: $transition-time-out;
        transition-timing-function: $transition-ease-out;
      }

      .modal__content {
        opacity: 0;
        transform-origin: 100% 0%;

        @media (max-width: #{$breakpoint-tablet}) {
          transform: translateY(100%);
        }
      }

      .modal__bg {
        backdrop-filter: blur(0) opacity(0)
      }
    }
  }

  .modal-enter-to,
  .modal-leave-from {
    transform: translateY(0);
    opacity: 1;
    transform-origin: 100% 0%;

    :local {
      &,
      .modal__content,
      .modal__bg {
        transition-property: transform, opacity, backdrop-filter;
        transition-duration: $transition-time-in;
        transition-timing-function: $transition-ease-in;
      }

      .modal__content {
        transform: translateY(0);
        opacity: 1;
      }

      .modal__bg {
        backdrop-filter: blur(1px) opacity(1);
      }
    }
  }
}
</style>
